import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Button, Box, Tabs, Tab,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem as MuiMenuItem,
  FormControl, InputLabel, Alert, CircularProgress, Tooltip, Divider,
  LinearProgress,
} from '@mui/material';
import {
  Add, Edit, Delete, School, People, Assignment,
  TrendingUp, Send, ContentCopy, Check, Email, CheckCircle, ErrorOutline,
  MailOutline,
} from '@mui/icons-material';
import SimpleGrid from '../components/SimpleGrid';
import { adminAPI, contentAPI } from '../services/api';

// ── Email validation ────────────────────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseEmails = (raw: string): string[] =>
  raw.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean);

const validateEmails = (emails: string[]): { valid: string[]; invalid: string[] } => {
  const valid: string[]   = [];
  const invalid: string[] = [];
  emails.forEach(e => (EMAIL_RE.test(e) ? valid : invalid).push(e));
  return { valid, invalid };
};

// ── Tab panel helper ────────────────────────────────────────────────────────
const TabPanel: React.FC<{ children?: React.ReactNode; index: number; value: number }> = ({
  children, value, index,
}) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

// ── Invite Learners Tab ─────────────────────────────────────────────────────
interface InviteResult { email: string; invite_link: string; status: string; }

const InviteLearnersTab: React.FC<{ courses: any[] }> = ({ courses }) => {
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [emailsRaw, setEmailsRaw]               = useState('');
  const [sending, setSending]                   = useState(false);
  const [result, setResult]                     = useState<{
    message: string; course_title: string; invites: InviteResult[];
  } | null>(null);
  const [error, setError]                       = useState<string | null>(null);
  const [copied, setCopied]                     = useState<Record<string, boolean>>({});

  const parsedEmails = parseEmails(emailsRaw);
  const { valid, invalid } = validateEmails(parsedEmails);
  const selectedCourse = courses.find(c => String(c.id) === selectedCourseId);

  const handleSend = async () => {
    if (!selectedCourseId || valid.length === 0) return;
    setSending(true);
    setError(null);
    setResult(null);
    try {
      const res = await adminAPI.inviteLearners(selectedCourseId, valid);
      setResult(res.data);
      setEmailsRaw('');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to send invitations');
    } finally {
      setSending(false);
    }
  };

  const copyLink = (link: string, email: string) => {
    navigator.clipboard.writeText(link);
    setCopied(prev => ({ ...prev, [email]: true }));
    setTimeout(() => setCopied(prev => ({ ...prev, [email]: false })), 2000);
  };

  const copyAll = () => {
    if (!result) return;
    const text = result.invites.map(i => `${i.email}: ${i.invite_link}`).join('\n');
    navigator.clipboard.writeText(text);
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Invite Learners</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Select a course, add learner email addresses, and send personalised invite links.
        Each learner will receive a direct link to log in and access the course.
      </Typography>

      <SimpleGrid container spacing={3}>
        {/* ── Left: form ── */}
        <SimpleGrid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 1 }}>
            <CardContent>
              {/* Course selector */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Select Course *</InputLabel>
                <Select
                  value={selectedCourseId}
                  label="Select Course *"
                  onChange={e => { setSelectedCourseId(e.target.value); setResult(null); setError(null); }}
                >
                  {courses.map(c => (
                    <MuiMenuItem key={c.id} value={String(c.id)}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{c.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.category} · {c.difficulty}
                        </Typography>
                      </Box>
                    </MuiMenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Selected course preview */}
              {selectedCourse && (
                <Alert severity="info" icon={<School />} sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight={600}>{selectedCourse.title}</Typography>
                  <Typography variant="caption">
                    {selectedCourse.modules?.length ?? 0} modules · {selectedCourse.difficulty} · {selectedCourse.category}
                  </Typography>
                </Alert>
              )}

              {/* Email input */}
              <TextField
                label="Learner Email Addresses"
                placeholder={"alice@company.com\nbob@company.com\ncarol@company.com"}
                multiline
                rows={8}
                fullWidth
                value={emailsRaw}
                onChange={e => { setEmailsRaw(e.target.value); setResult(null); setError(null); }}
                helperText="Enter one email per line, or separate with commas"
                sx={{ mb: 2 }}
              />

              {/* Parsed preview */}
              {parsedEmails.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {valid.length > 0 && (
                    <Chip icon={<CheckCircle />} label={`${valid.length} valid email${valid.length > 1 ? 's' : ''}`}
                      color="success" size="small" sx={{ mr: 1, mb: 1 }} />
                  )}
                  {invalid.length > 0 && (
                    <Chip icon={<ErrorOutline />} label={`${invalid.length} invalid: ${invalid.join(', ')}`}
                      color="error" size="small" sx={{ mb: 1 }} />
                  )}
                </Box>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <Send />}
                disabled={!selectedCourseId || valid.length === 0 || sending}
                onClick={handleSend}
                sx={{ py: 1.5, fontWeight: 700 }}
              >
                {sending ? 'Sending Invitations…' : `Send ${valid.length > 0 ? valid.length : ''} Invitation${valid.length !== 1 ? 's' : ''}`}
              </Button>
            </CardContent>
          </Card>
        </SimpleGrid>

        {/* ── Right: results ── */}
        <SimpleGrid item xs={12} md={6}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
          )}

          {result ? (
            <Card variant="outlined">
              <CardContent>
                <Alert severity="success" icon={<MailOutline />} sx={{ mb: 2 }}>
                  <Typography fontWeight={700}>{result.message}</Typography>
                  <Typography variant="caption">Course: {result.course_title}</Typography>
                </Alert>

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Invite Links ({result.invites.length})
                  </Typography>
                  <Button size="small" startIcon={<ContentCopy />} onClick={copyAll} variant="outlined">
                    Copy All
                  </Button>
                </Box>

                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Copy Link</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.invites.map(inv => (
                        <TableRow key={inv.email}>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2">{inv.email}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label="Sent" color="success" size="small" icon={<CheckCircle />} />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={copied[inv.email] ? 'Copied!' : inv.invite_link} arrow>
                              <IconButton size="small" onClick={() => copyLink(inv.invite_link, inv.email)}>
                                {copied[inv.email] ? <Check color="success" fontSize="small" /> : <ContentCopy fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  Note: In production, these links are automatically emailed to each learner.
                  In development mode, copy and share them manually.
                </Typography>

                <Button fullWidth variant="outlined" sx={{ mt: 2 }}
                  onClick={() => { setResult(null); setSelectedCourseId(''); }}>
                  Send Another Batch
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card variant="outlined" sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
              <CardContent sx={{ textAlign: 'center', color: 'text.secondary' }}>
                <MailOutline sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" gutterBottom>Invite results will appear here</Typography>
                <Typography variant="body2">
                  Select a course, enter email addresses, and click Send Invitations.
                </Typography>
              </CardContent>
            </Card>
          )}
        </SimpleGrid>
      </SimpleGrid>
    </Box>
  );
};

// ── Main AdminDashboard ─────────────────────────────────────────────────────

const AdminDashboard: React.FC = () => {
  const [users, setUsers]           = useState<any[]>([]);
  const [analytics, setAnalytics]   = useState<any>(null);
  const [courses, setCourses]       = useState<any[]>([]);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState(0);
  const [userError, setUserError]   = useState<string | null>(null);

  // Add user dialog
  const [addOpen, setAddOpen]       = useState(false);
  const [addForm, setAddForm]       = useState({ email: '', name: '', role: 'learner' });
  const [addLoading, setAddLoading] = useState(false);

  // Edit user dialog
  const [editOpen, setEditOpen]     = useState(false);
  const [editUser, setEditUser]     = useState<any>(null);
  const [editForm, setEditForm]     = useState({ name: '', role: '' });
  const [editLoading, setEditLoading] = useState(false);

  // Delete user dialog
  const [deleteUserOpen, setDeleteUserOpen] = useState(false);
  const [deleteUser, setDeleteUser]         = useState<any>(null);
  const [deleteLoading, setDeleteLoading]   = useState(false);

  // Course delete dialog (legacy)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse]     = useState<any>(null);

  useEffect(() => { loadAdminData(); }, []);

  const loadAdminData = async () => {
    try {
      const [usersRes, analyticsRes, coursesRes] = await Promise.all([
        adminAPI.getUsers(),
        adminAPI.getAnalytics(),
        contentAPI.getCourses(),
      ]);
      setUsers(usersRes.data.users || []);
      setAnalytics(analyticsRes.data);
      setCourses(coursesRes.data.courses || []);
    } catch (error) {
      console.error('Admin data load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!addForm.email.trim() || !addForm.name.trim()) return;
    setAddLoading(true);
    setUserError(null);
    try {
      const res = await adminAPI.createUser(addForm);
      setUsers(prev => [res.data.user, ...prev]);
      setAddOpen(false);
      setAddForm({ email: '', name: '', role: 'learner' });
    } catch (e: any) {
      setUserError(e?.response?.data?.error || 'Failed to create user');
    } finally {
      setAddLoading(false);
    }
  };

  const openEdit = (user: any) => {
    setEditUser(user);
    setEditForm({ name: user.name, role: user.role });
    setEditOpen(true);
  };

  const handleEditUser = async () => {
    if (!editUser) return;
    setEditLoading(true);
    setUserError(null);
    try {
      await adminAPI.updateUser(editUser.id, editForm);
      setUsers(prev => prev.map(u => u.id === editUser.id ? { ...u, ...editForm } : u));
      setEditOpen(false);
    } catch (e: any) {
      setUserError(e?.response?.data?.error || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const openDelete = (user: any) => {
    setDeleteUser(user);
    setDeleteUserOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    setDeleteLoading(true);
    try {
      await adminAPI.deleteUser(deleteUser.id);
      setUsers(prev => prev.filter(u => u.id !== deleteUser.id));
      setDeleteUserOpen(false);
    } catch (e: any) {
      setUserError(e?.response?.data?.error || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  const confirmDeleteCourse = () => {
    if (selectedCourse) {
      setCourses(prev => prev.filter(c => c.id !== selectedCourse.id));
      setDeleteDialogOpen(false);
      setSelectedCourse(null);
    }
  };

  if (loading) return <Container><Box sx={{ mt: 4 }}><LinearProgress /></Box></Container>;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Admin Dashboard</Typography>

      {/* Stats */}
      {analytics && (
        <SimpleGrid container spacing={3} sx={{ mb: 4 }}>
          {[
            { icon: <People sx={{ fontSize: 40 }} />, color: 'primary.main',   value: analytics.total_users,         label: 'Total Users' },
            { icon: <TrendingUp sx={{ fontSize: 40 }} />, color: 'success.main', value: analytics.active_users,       label: 'Active Users' },
            { icon: <School sx={{ fontSize: 40 }} />, color: 'info.main',       value: courses.length,               label: 'Courses' },
            { icon: <Assignment sx={{ fontSize: 40 }} />, color: 'warning.main', value: analytics.certificates_issued, label: 'Certificates' },
          ].map(({ icon, color, value, label }) => (
            <SimpleGrid item xs={6} sm={3} key={label}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ color }}>{icon}</Box>
                  <Typography variant="h4" sx={{ color, fontWeight: 700 }}>{value}</Typography>
                  <Typography color="text.secondary">{label}</Typography>
                </CardContent>
              </Card>
            </SimpleGrid>
          ))}
        </SimpleGrid>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Users" />
          <Tab label="Invite Learners" icon={<Send fontSize="small" />} iconPosition="start" />
          <Tab label="Analytics" />
        </Tabs>
      </Box>

      {/* Tab 0 — Users */}
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Users ({users.length})</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setAddOpen(true)}>Add User</Button>
        </Box>
        {userError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUserError(null)}>{userError}</Alert>}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Profile Completed</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map(user => (
                <TableRow key={user.id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip label={user.role} size="small" color={user.role === 'admin' ? 'error' : 'primary'} />
                  </TableCell>
                  <TableCell>
                    <Chip label={user.profile_completed ? 'Yes' : 'No'} size="small"
                      color={user.profile_completed ? 'success' : 'warning'} />
                  </TableCell>
                  <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <IconButton size="small" sx={{ mr: 1 }} onClick={() => openEdit(user)}><Edit /></IconButton>
                    <IconButton size="small" color="error" onClick={() => openDelete(user)}><Delete /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab 1 — Invite Learners */}
      <TabPanel value={activeTab} index={1}>
        <InviteLearnersTab courses={courses} />
      </TabPanel>

      {/* Tab 2 — Analytics */}
      <TabPanel value={activeTab} index={2}>
        <Typography variant="h5" gutterBottom>Analytics & Reports</Typography>
        <SimpleGrid container spacing={3}>
          <SimpleGrid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>User Engagement</Typography>
                <Typography variant="body2" color="text.secondary">
                  Detailed analytics will be implemented in the next version.
                </Typography>
              </CardContent>
            </Card>
          </SimpleGrid>
          <SimpleGrid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Course Performance</Typography>
                <Typography variant="body2" color="text.secondary">
                  Course completion rates and performance metrics coming soon.
                </Typography>
              </CardContent>
            </Card>
          </SimpleGrid>
        </SimpleGrid>
      </TabPanel>

      {/* Delete course confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Course</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete "{selectedCourse?.title}"? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteCourse} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField label="Full Name *" fullWidth value={addForm.name}
              onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} autoFocus />
            <TextField label="Email *" fullWidth value={addForm.email}
              onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))} />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select value={addForm.role} label="Role"
                onChange={e => setAddForm(p => ({ ...p, role: e.target.value }))}>
                <MuiMenuItem value="learner">Learner</MuiMenuItem>
                <MuiMenuItem value="content_manager">Content Manager</MuiMenuItem>
                <MuiMenuItem value="admin">Admin</MuiMenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddUser}
            disabled={!addForm.email.trim() || !addForm.name.trim() || addLoading}>
            {addLoading ? <CircularProgress size={20} /> : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField label="Full Name" fullWidth value={editForm.name}
              onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} autoFocus />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select value={editForm.role} label="Role"
                onChange={e => setEditForm(p => ({ ...p, role: e.target.value }))}>
                <MuiMenuItem value="learner">Learner</MuiMenuItem>
                <MuiMenuItem value="content_manager">Content Manager</MuiMenuItem>
                <MuiMenuItem value="admin">Admin</MuiMenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditUser} disabled={editLoading}>
            {editLoading ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteUserOpen} onClose={() => setDeleteUserOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <strong>{deleteUser?.name}</strong> ({deleteUser?.email})? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUserOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;
